import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { AppliedScenario } from "@/types/applied-scenario.types";
import type {
  AwaitingClassificationPage,
  CalibrationPendencyItem,
  CalibrationPendencyKind,
  CalibrationPendencyPage,
  CalibrationPendencyStatus,
  ConfirmClassificationInput,
  ResolvePendencyInput,
} from "@/types/closed-loop.types";
import { trackRecordKey } from "./track-record/hooks";

// Spec 077 — o CICLO FECHADO no cliente.
//
// ⚠️ SEM `onMutate` otimista, de propósito: a resposta do servidor traz a trava recalculada
// (`allowedCauses`), a proposta congelada e o histórico. Adivinhá-los na tela seria reimplementar
// no cliente a regra que a trava existe para centralizar — e a primeira divergência entre as duas
// implementações apareceria como uma causa habilitada que o servidor recusa com 422.
//
// ⚠️ As chaves NÃO casam por prefixo entre si: o detalhe é `["applied-scenario", id]` (singular) e
// a lista da linha é `["applied-scenarios", lineId]` (plural). Invalidar as duas é obrigatório, não
// redundante — é a mesma armadilha da run ativa.

export const awaitingClassificationKey = ["closed-loop", "awaiting-classification"] as const;
export const pendenciesKey = ["closed-loop", "pendencies"] as const;

/**
 * O tamanho da página das duas filas — o mesmo default dos dois contratos.
 *
 * ⚠️ As filas PAGINAM, e a paginação não é opcional: as abas exibem o `total` REAL do servidor
 * (FR-037), então sem controle de página a tela dizia "(60)" e desenhava 25, com 35 itens
 * inalcançáveis. Pior: a ordem é a mais antiga primeiro (FR-038), então o que fica invisível é
 * sempre o trabalho recém-chegado, e ele nunca aparece por conta própria.
 */
export const QUEUE_PAGE_SIZE = 25;

export function useConfirmClassification() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appliedScenarioId, lineId: _lineId, ...body }: ConfirmClassificationInput) =>
      api.post<AppliedScenario>(
        `/api/v1/applied-scenarios/${appliedScenarioId}/classification`,
        body
      ),
    onSuccess: (_data, { appliedScenarioId, lineId }) => {
      // As três chaves de R10 — o compromisso classificado sai de uma fila e pode entrar na outra.
      void qc.invalidateQueries({ queryKey: ["applied-scenario", appliedScenarioId] });
      void qc.invalidateQueries({ queryKey: ["applied-scenarios", lineId] });
      void qc.invalidateQueries({ queryKey: awaitingClassificationKey });
      void qc.invalidateQueries({ queryKey: pendenciesKey });
      // Spec 079 — a RÉGUA. Confirmar a causa é o ato que faz o compromisso passar a contar: ele
      // sai de "aguardando julgamento" e entra (ou não, se for `EXECUTION_NON_ADHERENCE`) na
      // amostra testada. `["closed-loop","track-record"]` NÃO casa por prefixo com as duas chaves
      // acima, então é preciso nomeá-la.
      void qc.invalidateQueries({ queryKey: trackRecordKey });
    },
    // O 422 da trava (FR-011) e o da justificativa faltando precisam APARECER: são recusas com
    // saída, e o `MutationCache` global já as traduz por `getFriendlyErrorMessage`.
    meta: { errorTitle: "Não foi possível registrar a causa" },
  });
}

/** Os filtros da fila de pendências, como a tela os controla (FR-041). */
export interface PendencyFilterState {
  status: CalibrationPendencyStatus;
  lineId?: string;
  kind?: CalibrationPendencyKind;
}

function queryString(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) qs.set(k, v);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * A fila 1 — apurados que ninguém julgou. Cross-linha por desenho.
 *
 * A chave inclui os filtros: duas filtragens diferentes são duas listas diferentes, e compartilhar
 * a chave faria a segunda mostrar o resultado da primeira até o refetch chegar.
 */
export function useAwaitingClassification(lineId?: string, offset = 0) {
  return useQuery<AwaitingClassificationPage>({
    // O `offset` entra na chave: duas páginas são dois recursos, e compartilhar a chave faria a
    // segunda mostrar o conteúdo da primeira até o refetch chegar. A invalidação por PREFIXO
    // (`awaitingClassificationKey`) continua alcançando todas.
    queryKey: [...awaitingClassificationKey, lineId ?? null, offset],
    queryFn: () =>
      api.get<AwaitingClassificationPage>(
        `/api/v1/applied-scenarios/awaiting-classification${queryString({
          lineId,
          limit: String(QUEUE_PAGE_SIZE),
          offset: offset ? String(offset) : undefined,
        })}`
      ),
  });
}

/** A fila 2 — o trabalho de calibração que as causas acionáveis geraram. */
export function usePendencies(filters: PendencyFilterState, offset = 0) {
  return useQuery<CalibrationPendencyPage>({
    queryKey: [
      ...pendenciesKey,
      filters.status,
      filters.lineId ?? null,
      filters.kind ?? null,
      offset,
    ],
    queryFn: () =>
      api.get<CalibrationPendencyPage>(
        `/api/v1/calibration-pendencies${queryString({
          status: filters.status,
          lineId: filters.lineId,
          kind: filters.kind,
          limit: String(QUEUE_PAGE_SIZE),
          offset: offset ? String(offset) : undefined,
        })}`
      ),
  });
}

/**
 * Resolver ou descartar. Invalida a fila de pendências E o detalhe do compromisso de origem — o
 * histórico de lá mostra a pendência de cada classificação, e ela acabou de mudar de desfecho.
 */
export function useResolvePendency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: ResolvePendencyInput) =>
      api.post<CalibrationPendencyItem>(`/api/v1/calibration-pendencies/${id}/resolution`, body),
    onSuccess: (_data, { appliedScenarioId }) => {
      void qc.invalidateQueries({ queryKey: pendenciesKey });
      void qc.invalidateQueries({ queryKey: ["applied-scenario", appliedScenarioId] });
      // Spec 079 — a invalidação MENOS ÓBVIA e a mais importante da tela. Resolver não escreve
      // nenhuma linha da régua: move o MARCO ZERO, o que redefine quais compromissos contam e muda
      // todos os números daquela linha de uma vez. Sem isto, a aba segue exibindo a régua do gêmeo
      // de ANTES do conserto — e credita ao modelo novo o desempenho do que acabou de ser
      // substituído. Descartar/superar não movem o marco, mas passam pelo mesmo hook.
      void qc.invalidateQueries({ queryKey: trackRecordKey });
    },
    // O 409 de "já encerrada" precisa aparecer: é o que uma fila desatualizada produz, e a mensagem
    // do servidor NOMEIA o desfecho que a pendência já teve.
    meta: { errorTitle: "Não foi possível encerrar a pendência" },
  });
}
